import { ComponentType, ReactNode, Ref, useState } from 'react';

import {
  Avatar,
  Card,
  CardContent,
  CardProps,
  Collapse,
  FormControlLabel,
  FormControlLabelProps,
  IconButton,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';

import {
  RiArrowUpSLine as ExpandLess,
  RiArrowDownSLine as ExpandMore,
  RiQuestionFill as HelpOutline,
} from '@remixicon/react';
import classNames from 'classnames';

import { useMeasureDimensions } from '../../hooks';

type CardLabelMuiProps = Omit<FormControlLabelProps, 'label'> & {
  src?: string;
  label: string;
  description?: string;
  icon?: ComponentType<{ className?: string }>;
  variant?: CardProps['variant'];
  expandedContent?: ReactNode;
  disablePadding?: boolean;
};

const useStylesMui = makeStyles(theme => ({
  root: {
    margin: theme.spacing(2, 0, 2, 1),
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    '&:first-child &': {
      marginTop: 0,
    },
    '&:last-child &': {
      marginBottom: 0,
    },
    // default to size of image + padding, overwrite with style based on actual height
    gridTemplateRows: `${40 + theme.spacing(8)}px auto`,
    [theme.breakpoints.up('sm')]: {
      gridTemplateRows: `${60 + theme.spacing(8)}px auto`,
    },
    '&$disablePadding': {
      marginLeft: 0,
    },
    '&$disablePadding $label': {
      marginLeft: 0,
    },
  },
  disablePadding: {
    padding: 0,
  },
  label: {
    gridRow: '1',
    gridRowEnd: '-1',
    gridColumn: '2',
    width: '100%',
    marginLeft: theme.spacing(1),
  },
  image: {
    height: 54,
    width: 54,
    objectFit: 'contain',
    objectPosition: 'center',
    [theme.breakpoints.up('sm')]: {
      height: 80,
      width: 80,
    },
  },
  icon: {
    height: 32,
    width: 32,
    [theme.breakpoints.up('sm')]: {
      height: 54,
      width: 54,
    },
  },
  disabled: {
    opacity: 0.7,
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
  },
  textContainer: {
    flex: 1,
  },
  card: {
    transition: theme.transitions.create('border-color'),
    border: '1px solid transparent',
    padding: theme.spacing(2),
    '.Mui-checked + $label &': {
      border: `1px solid ${theme.palette.text.primary}`,
    },
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  expandedContent: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

/** @deprecated Legacy MUI copy — migrate consumers to the Encore FormControlCardLabel and delete. */
export const FormControlCardLabelMui = ({
  src,
  label,
  description,
  icon: Icon = HelpOutline,
  variant,
  expandedContent,
  disablePadding = false,
  ...formControlLabelProps
}: CardLabelMuiProps) => {
  const classes = useStylesMui();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [ref, { height }] = useMeasureDimensions<HTMLDivElement>({
    measureWidth: false,
    measureHeight: true,
  });

  return (
    <FormControlLabel
      {...formControlLabelProps}
      className={classNames(
        classes.root,
        disablePadding && classes.disablePadding,
        formControlLabelProps.className,
      )}
      classes={{
        root: classes.root,
        disabled: classes.disabled,
        label: classes.label,
      }}
      style={{
        gridTemplateRows: height
          ? `${height + theme.spacing(8)}px auto`
          : undefined,
      }}
      label={
        <Card variant={variant} className={classes.card}>
          <CardContent className={classes.cardContent}>
            <div className={classes.wrapper} ref={ref as Ref<HTMLDivElement>}>
              {src ? (
                <img src={src} alt={label} className={classes.image} />
              ) : (
                <Avatar src={src} className={classes.image}>
                  <Icon className={classes.icon} />
                </Avatar>
              )}
              <div className={classes.textContainer}>
                <Typography variant="h6">{label}</Typography>
                {description && (
                  <Typography variant="body1" color="textSecondary">
                    {description}
                  </Typography>
                )}
              </div>
              {expandedContent ? (
                <IconButton
                  size="small"
                  onClick={() => setExpanded(current => !current)}
                >
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              ) : null}
            </div>
            <Collapse
              unmountOnExit
              in={expanded}
              className={classes.expandedContent}
            >
              {expandedContent}
            </Collapse>
          </CardContent>
        </Card>
      }
    />
  );
};
